"use client";

import React, { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "./Logo";
import Icon from "@/components/ui/Icon";

import ThemeToggle from "./ThemeToggle";

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navHeight, setNavHeight] = useState(65);
  const navRef = useRef<HTMLElement>(null);
  const pathname = usePathname();

  // Measure actual navbar height
  useEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) {
        setNavHeight(navRef.current.offsetHeight);
      }
    };
    updateNavHeight();
    window.addEventListener("resize", updateNavHeight);
    return () => window.removeEventListener("resize", updateNavHeight);
  }, []);

  // Close mobile menu on route change
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    setIsMobileMenuOpen(false);
  }

  // Lock body scroll and allow ESC key to close menu
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  const links = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Services", href: "/services" },
    { name: "Portfolio", href: "/portfolio" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" }
  ];

  const isActive = (href: string) => {
    if (href === "/") {
      return pathname === "/";
    }
    if (href === "/services") {
      return pathname.startsWith("/services");
    }
    if (href === "/portfolio") {
      return pathname.startsWith("/portfolio");
    }
    if (href === "/blog") {
      return pathname.startsWith("/blog");
    }
    return pathname === href;
  };

  return (
    <>
      <nav
        ref={navRef}
        className="bg-surface/80 supports-[backdrop-filter]:bg-surface/70 sticky top-0 z-50 border-b border-outline-variant/30 backdrop-blur-xl transition-all duration-300"
      >
        <div className="flex justify-between items-center w-full px-margin-mobile md:px-gutter max-w-container-max mx-auto py-3.5">
          <Link href="/" className="transition-transform hover:scale-[1.02]">
            <Logo />
          </Link>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1.5 bg-surface-container/50 p-1.5 rounded-full border border-outline-variant/30 backdrop-blur-md">
            {links.map((link) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.name}
                  href={link.href}
                  onClick={() => {
                    if (link.href === "/blog" && typeof window !== "undefined") {
                      sessionStorage.removeItem("last_viewed_blog");
                      if (pathname === "/blog") {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }
                  }}
                  className={`relative px-4 py-1.5 rounded-full text-sm font-bold transition-colors duration-200 ${
                    active ? "text-primary" : "text-on-surface hover:text-primary"
                  }`}
                >
                  {active && (
                    <motion.div
                      layoutId="active-pill"
                      className="absolute inset-0 bg-primary/10 rounded-full border border-primary/30"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{link.name}</span>
                </Link>
              );
            })}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <ThemeToggle />
            <Link 
              href="/book-consultation"
              className="inline-block bg-primary hover:bg-primary-hover text-on-primary px-5 py-2 rounded-lg font-bold text-sm transition-all cursor-pointer shadow-[0_6px_20px_-6px_var(--color-primary)] hover:shadow-[0_8px_25px_-5px_var(--color-primary)] hover:-translate-y-0.5"
            >
              Book Consultation
            </Link>
          </div>

          {/* Mobile Hamburger Button & ThemeToggle */}
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-on-surface hover:bg-surface-container transition-colors cursor-pointer"
              aria-label={isMobileMenuOpen ? "Close Menu" : "Open Menu"}
            >
              <Icon name={isMobileMenuOpen ? "close" : "menu"} size={26} aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu - Full Width, Opens Below Header from Right Side */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            style={{ top: navHeight }}
            className="fixed inset-x-0 bottom-0 z-40 bg-surface/98 backdrop-blur-2xl md:hidden flex flex-col justify-between overflow-hidden shadow-2xl"
          >
            {/* Navigation Links (Scrollable area) */}
            <div className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-2">
              {links.map((link) => {
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.name}
                    href={link.href}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      if (link.href === "/blog" && typeof window !== "undefined") {
                        sessionStorage.removeItem("last_viewed_blog");
                        if (pathname === "/blog") {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }
                    }}
                    className={`text-2xl font-bold py-3.5 border-b border-outline-variant/30 flex items-center justify-between transition-colors ${
                      active ? "text-primary" : "text-on-surface hover:text-primary"
                    }`}
                  >
                    <span>{link.name}</span>
                    {active && (
                      <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Mobile Menu Footer */}
            <div className="p-6 border-t border-outline-variant/30 flex flex-col gap-4 shrink-0 bg-surface">
              <Link
                href="/book-consultation"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full text-center bg-primary hover:bg-primary-hover text-on-primary py-3.5 rounded-xl font-bold text-base shadow-md transition-all active:scale-[0.98]"
              >
                Book Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
